/* eslint-disable import/prefer-default-export */
import { graphql, useStaticQuery } from 'gatsby';
import { useState, useEffect } from 'react';

const THEME_MODE = 'theme-mode';
const THEME_CLASSES = ['rs-theme-light', 'rs-theme-dark'];

const getThemeMode = () => {
  if (typeof window === 'undefined') return 'light';
  const savedThemeMode = window.localStorage.getItem(THEME_MODE);
  if (savedThemeMode === 'dark' || savedThemeMode === 'light') return savedThemeMode;
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

// Mirrors the inline script in gatsby-ssr.js, which applies the initial mode
// before the first paint so there is no light flash on full page loads.
const applyThemeMode = (mode) => {
  const { body, documentElement } = window.document;
  body.classList.remove(...THEME_CLASSES);
  body.classList.add(`rs-theme-${mode}`);
  documentElement.style.colorScheme = mode;
};

export const useTheme = () => {
  const [themeMode, setThemeMode] = useState(getThemeMode);

  useEffect(() => {
    if (getThemeMode() !== themeMode) {
      window.localStorage.setItem(THEME_MODE, themeMode);
    }
    applyThemeMode(themeMode);
  }, [themeMode]);

  return [themeMode, setThemeMode];
};

/**
 * custom hook to detect the window size of a browser
 * @return {Array} [height, width ].
 */
export const useWindowSize = () => {
  const [size, setSize] = useState([0, 0]);
  useEffect(() => {
    function updateSize() {
      setSize([window.innerWidth, window.innerHeight]);
    }

    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  return size;
};

export const useSiteMetadata = () => {
  const data = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          pathPrefix
          siteUrl
          title
          description
          author
          authorAlternative
          introduction
          avatar
          avatarFlip
          professions
          tocMaxDepth
          excerptMaxLength
          birthday
          location
          email
          postsForArchivePage
          social {
            url
            icon
          }
          disqusScript
          contactFormUrl
          pages {
            home
            posts
            contact
            resume
            tags
            research
          }
          wakatime {
            username
            activity
            language
            editor
            os
          }
          interests {
            icon
            title
          }
          news {
            date
            text
          }
          education {
            date
            icon
            title
            location
            logo
          }
          experience {
            title
            position
            data {
              date
              title
              location
              description
              tags
              logo
              icon
            }
          }
          awards {
            date
            title
            location
            logo
          }
          tags {
            id
            name
            description
            color
          }
        }
      }
    }
  `);
  return data.site.siteMetadata;
};
